import { type CommunicationChannel } from '../types/communications';
import { SubscriptionTokenKey } from '../types/subscriptionGroup';
import { type DecodedSubToken } from '../types/userSubscriptionPreference';

function base64UrlToBase64(input: string): string {
    const replaced = input.replace(/-/g, '+').replace(/_/g, '/');
    const padLen = (4 - (replaced.length % 4)) % 4;
    return replaced + '='.repeat(padLen);
}

function decodeBase64ToUtf8(base64: string): string {
    if (typeof atob === 'function') {
        const binary = atob(base64);
        const bytes = Uint8Array.from({ length: binary.length }, (_v, i) =>
            binary.charCodeAt(i),
        );
        const decoder = new TextDecoder('utf-8');
        return decoder.decode(bytes);
    }
    return Buffer.from(base64, 'base64').toString('utf-8');
}

function base64ToBase64Url(base64: string): string {
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function encodeUtf8ToBase64(utf8: string): string {
    if (typeof btoa === 'function') {
        const encoder = new TextEncoder();
        const bytes = encoder.encode(utf8);
        const binary = Array.from(bytes, (byte) =>
            String.fromCharCode(byte),
        ).join('');
        return btoa(binary);
    }
    return Buffer.from(utf8, 'utf-8').toString('base64');
}

export function decodeSubToken(token: string): DecodedSubToken | null {
    if (!token || token.trim().length === 0) {
        return null;
    }
    const base64 = base64UrlToBase64(token);
    const jsonStr = decodeBase64ToUtf8(base64);

    let parsed: unknown;
    try {
        parsed = JSON.parse(jsonStr);
    } catch {
        throw new Error('Invalid subToken: not JSON');
    }

    const obj = parsed as Record<string, unknown>;
    const u = obj?.[SubscriptionTokenKey.UserId];
    const p = obj?.[SubscriptionTokenKey.ProjectId];
    const c: CommunicationChannel = obj?.[
        SubscriptionTokenKey.Channel
    ] as CommunicationChannel;
    const i = obj?.[SubscriptionTokenKey.ChannelIdentifier];
    const g = obj?.[SubscriptionTokenKey.SubscriptionGroupId];
    const a = obj?.[SubscriptionTokenKey.CampaignId];

    if (
        typeof u === 'string' &&
        typeof p === 'string' &&
        typeof c === 'string' &&
        typeof i === 'string'
    ) {
        return {
            userId: u,
            projectId: p,
            channel: c,
            channelIdentifier: i,
            subGroupId: typeof g === 'string' ? g : undefined,
            campaignId: typeof a === 'string' ? a : undefined,
        };
    }
    throw new Error('Invalid subToken: missing required fields');
}

export function encodeSubToken(data: DecodedSubToken): string {
    if (!data.userId || !data.projectId || !data.channel) {
        throw new Error(
            'Missing required fields: userId, projectId, channel are required',
        );
    }

    const tokenObj: Record<string, string> = {
        [SubscriptionTokenKey.UserId]: data.userId,
        [SubscriptionTokenKey.ProjectId]: data.projectId,
        [SubscriptionTokenKey.Channel]: data.channel,
        [SubscriptionTokenKey.ChannelIdentifier]: data.channelIdentifier,
    };

    if (data.subGroupId) {
        tokenObj[SubscriptionTokenKey.SubscriptionGroupId] = data.subGroupId;
    }
    if (data.campaignId) {
        tokenObj[SubscriptionTokenKey.CampaignId] = data.campaignId;
    }

    const jsonStr = JSON.stringify(tokenObj);
    const base64 = encodeUtf8ToBase64(jsonStr);
    return base64ToBase64Url(base64);
}
